import { history } from '@app/app.routes';
import store from '@app/app.store';
import * as maerskLogo from '@app/assets/images/maersk-logo-rgb.svg';
import Icon from '@app/components/shared/icon.component';
import * as models from '@app/models';
import { IModuleType, initialSidebarState, Module } from '@app/models';
import { withAppModule } from '@app/providers/app-module.provider';
import {
	Box,
	Collapse,
	Drawer,
	Hidden,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@material-ui/core';
import _ from 'lodash';
import React from 'react';
import { injectIntl } from 'react-intl';

class SidebarComponent extends React.Component<models.ISidebarComponentProps, models.ISidebarState> {
	constructor(props: models.ISidebarComponentProps) {
		super(props);
		this.bindFunctions();
		this.state = initialSidebarState();
	}

	public componentDidMount() {
		// Auto expand parent module on refresh

		const parentModule = this.props.AppModule.modules
			.filter((x) => x.location === 'sidebar')
			.find((x) => x.name === this.props.AppModule.module.parentName);
		if (parentModule) {
			const collapse = this.state.collapse;
			collapse[parentModule.name] = true;
			this.setState({
				collapse,
			});
		}
	}

	public componentDidUpdate(
		prevProps: Readonly<models.ISidebarComponentProps>,
		prevState: Readonly<models.ISidebarState>
	) {
		if (prevProps.open && !this.props.open) {
			this.setState((currentState) => ({
				...currentState,
				collapse: {
					Reports: false,
				},
			}));
		}
	}

	public render() {
		const reportModule = this.props.AppModule.modules
			.filter((x) => x.location === 'sidebar')
			.find((x) => x.name === 'Reports');
		if (!!reportModule) {
			reportModule.collapse = this.state.collapse.Reports;
		}

		return (
			<Drawer
				variant={this.props.variant || 'permanent'}
				className={`sidebar-root ${this.props.open ? 'sidebar-opened' : 'sidebar-closed'}`}
				classes={{
					paper: `sidebar-drawer ${this.props.open ? 'sidebar-opened' : 'sidebar-closed'}`,
				}}
				open={this.props.open}
				onClose={this.props.onToggle}
			>
				<Box>
					<List disablePadding={true}>
						<Hidden smUp={true}>
							<ListItem className='list-item genesis-text'>
								<img src={maerskLogo} className='genesis-logo' />
							</ListItem>
						</Hidden>
						{this.props.AppModule.modules
							.filter((x) => x.location === 'sidebar')
							.map((module) => (
								<React.Fragment key={module.name}>
									<ListItem
										button={true}
										className={this.getClassNames(module)}
										onClick={() => this.onListItemClick(module)}
									>
										<ListItemIcon>
											<Icon {...module.icon} />
										</ListItemIcon>
										{this.props.open && (
											<ListItemText
												primaryTypographyProps={{
													noWrap: true,
												}}
												primary={module.toString()}
												classes={{
													primary: 'list-item-text',
												}}
											/>
										)}
										{this.props.open &&
											module.isParent &&
											(module.collapse ? (
												<Icon name='chevron-up' className='icon-dimension-square' />
											) : (
												<Icon name='chevron-down' className='icon-dimension-square' />
											))}
									</ListItem>
									{module.isParent && (
										<Collapse in={module.collapse} timeout='auto' className='list-item-collapse' unmountOnExit={true}>
											<List component='div' disablePadding={true}>
												{_.values(module.children)
													.filter(
														(x) =>
															x.location === 'sidebar' &&
															(x.userRoles === 'all' || x.userRoles.includes(store.getState().authentication.user.role))
													)
													.sort((a, b) => a.sortOrder - b.sortOrder)
													.map((child) => (
														<ListItem
															key={child.name}
															button={true}
															className={this.getClassNames(child, true)}
															onClick={() => this.onListItemClick(child)}
														>
															<ListItemIcon>
																<Icon {...child.icon} />
															</ListItemIcon>
															{this.props.open && (
																<ListItemText
																	primaryTypographyProps={{
																		noWrap: true,
																	}}
																	primary={child.toString()}
																	classes={{
																		primary: 'list-item-text',
																	}}
																/>
															)}
														</ListItem>
													))}
											</List>
										</Collapse>
									)}
								</React.Fragment>
							))}
					</List>
				</Box>
				<Box justifyContent={this.props.open ? 'space-between' : 'center'} className='footer'>
					<Hidden smUp={true}>
						<Typography className='version' variant='caption'>
							{'Version: ' + this.props.version}
						</Typography>
					</Hidden>
					<Hidden xsDown={true}>
						{this.props.open && (
							<Typography variant='caption' noWrap={true} className='version'>
								{'Version: ' + this.props.version}
							</Typography>
						)}
						<ListItemIcon>
							<IconButton onClick={this.handleToggle}>
								{this.props.open ? <Icon name='double-chevron-left' /> : <Icon name='double-chevron-right' />}
							</IconButton>
						</ListItemIcon>
					</Hidden>
				</Box>
			</Drawer>
		);
	}

	private onListItemClick(module: IModuleType) {
		const { collapse } = this.state;
		switch (module) {
			case Module.Apps:
			case Module.Shipments:
			case Module.ShipmentManifestComponent:
			case Module.AdminShipments:
			case Module.Dashboard:
			case Module.Reports.children.ReportShipment:
			case Module.Reports.children.ReportBalanceHistory:
			case Module.Reports.children.ReportAccountStatement:
			case Module.Reports.children.ReportAccountStatementByDay:
			case Module.Reports.children.ReportDailyFinance:
			case Module.Reports.children.ReportMonthlyFinance:
			case Module.Customers:
			case Module.Accounts:
			case Module.AccountDetails:
			case Module.AccountAppDetails:
			case Module.Settings:
			case Module.Carriers:
			case Module.CarrierAccounts:
				// In small screen where the variant is temporary,
				// we want to toggle the sidebar to close.
				if (this.props.variant === 'temporary') {
					this.props.onToggle();
				}

				history.push(module.url);
				break;
			case Module.Reports:
				// In big screen where the variant is permanent,
				// we want to open the sidebar AND toogle the collapse when reports is clicked.
				// And in small screen we just want to toggle the collapse.
				if (!this.props.open && this.props.variant === 'permanent') {
					this.props.onSidebarOpen();
				}
				this.setState({
					collapse: {
						...collapse,
						Reports: !collapse.Reports,
					},
				});
				break;
		}
	}

	private getClassNames(module: IModuleType, isChildModule: boolean = false): string {
		let classes = 'list-item';
		if (this.props.AppModule.module === module) {
			classes += ' active';
		}
		if (isChildModule) {
			classes += ' list-item-child';
		}
		if (this.props.AppModule.module.parentName === module.name) {
			classes += ' active';
		}
		return classes;
	}

	private handleToggle() {
		// When closing the sidebar we also want to collapse the report items
		this.setState((currentState) => ({
			collapse: {
				...currentState.collapse,
				Reports: false,
			},
		}));
		this.props.onToggle();
	}

	private bindFunctions() {
		this.onListItemClick = this.onListItemClick.bind(this);
		this.getClassNames = this.getClassNames.bind(this);
		this.handleToggle = this.handleToggle.bind(this);
	}
}

export default injectIntl(withAppModule(SidebarComponent));



// WEBPACK FOOTER //
// ./src/components/core/sidebar/sidebar.component.tsx